import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Editor } from '@/components/editor/BlockNoteEditor';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface SharedDocumentData {
  id: string;
  title: string;
  content: string;
  canEdit: boolean;
  canDownload: boolean;
}

export default function SharedDocument() {
  const { token } = useParams<{ token: string }>();
  const [document, setDocument] = useState<SharedDocumentData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await fetch(`/api/share?token=${token}`, {
          credentials: 'include',
        });
        const data = await response.json();

        if (data.success) {
          setDocument(data.document);
        } else {
          setError(data.error || 'Documento no encontrado');
        }
      } catch {
        setError('Error al cargar el documento');
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchDocument();
    }
  }, [token]);

  const saveDocument = async (content: string) => {
    if (!document || !document.canEdit) return;

    try {
      await fetch('https://memories-api.haliagav.workers.dev/api/share', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token,
          content,
        }),
      });
    } catch {
      toast.error('Error al guardar');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Error</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Documento no encontrado</h1>
          <p className="text-muted-foreground">El documento que buscas no existe o ha sido eliminado.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <h1 className="text-xl font-semibold">{document.title}</h1>
          <p className="text-sm text-muted-foreground">
            {document.canEdit ? 'Puedes editar este documento' : 'Solo lectura'}
          </p>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white dark:bg-background rounded-lg shadow-sm border p-6">
          <Editor
            initialContent={document.content}
            onSave={saveDocument}
            editable={document.canEdit}
          />
        </div>
      </main>
    </div>
  );
}